import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import ServiceTypeStep from "./steps/ServiceTypeStep";
import CityStep from "./steps/CityStep";
import DateStep from "./steps/DateStep";
import MapView from "./MapView";
import ShopsList from "./ShopsList";
import CarSelectionStep from "./steps/CarSelectionStep";
import VerificationStep from "./steps/VerificationStep";
import { useCarContext } from "../../../contexts/CarContext";
import axios from "axios";
import { auth } from "../../../firebase";
import "dayjs/locale/ro";
import {
  MapPinIcon,
  CalendarIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  CheckCircleIcon,
  BuildingOfficeIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

const AppointmentsMap = () => {
  const { cars } = useCarContext();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedService, setSelectedService] = useState("");
  const [selectedServiceName, setSelectedServiceName] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedShop, setSelectedShop] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [cities, setCities] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDesktopView, setIsDesktopView] = useState(false);

  const serviceTypes = [
    { id: "ITP", name: "ITP", icon: WrenchScrewdriverIcon },
    {
      id: "MECHANICAL",
      name: "Reparație Mecanică",
      icon: WrenchScrewdriverIcon,
    },
  ];

  const steps = [
    {
      label: "Serviciu",
      icon: WrenchScrewdriverIcon,
      completed: !!selectedService,
    },
    { label: "Oraș", icon: MapPinIcon, completed: !!selectedCity },
    { label: "Data", icon: CalendarIcon, completed: !!selectedDate },
    { label: "Service", icon: BuildingStorefrontIcon, completed: !!selectedShop && !!selectedTimeSlot },
    { label: "Vehicul", icon: TruckIcon, completed: !!selectedCar },
    { label: "Confirmare", icon: CheckCircleIcon, completed: false },
  ];

  useEffect(() => {
    const checkViewport = () => {
      setIsDesktopView(window.innerWidth >= 1024);
    };

    checkViewport();
    window.addEventListener("resize", checkViewport);

    return () => window.removeEventListener("resize", checkViewport);
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(
          "https://api.servicegest.ro/online/cities"
        );
        if (response.ok) {
          const data = await response.json();
          // Handle potential duplicates with different casing or whitespace
          const seen = new Set();
          const uniqueCities = data.filter((city) => {
            const normalized = city.trim().toLowerCase();
            if (seen.has(normalized)) {
              return false;
            } else {
              seen.add(normalized);
              return true;
            }
          });
          setCities(uniqueCities);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };

    fetchCities();
  }, []);

  // Set service name when service changes
  useEffect(() => {
    if (selectedService) {
      const service = serviceTypes.find((s) => s.id === selectedService);
      if (service) {
        setSelectedServiceName(service.name);
      }
    }
  }, [selectedService, serviceTypes]);

  const handleChangeFilters = () => {
    setCurrentStep(0);
    setShops([]);
    setSelectedShop(null);
    setSelectedTimeSlot(null);
    setSelectedCar(null);
    setAppointmentId(null);
  };

  // Handle shop and time slot selection
  const handleSelectShopAndTime = (shop, timeSlot) => {
    setSelectedShop(shop);
    setSelectedTimeSlot(timeSlot);
    setCurrentStep(4); // Move to car selection step
  };

  // Create appointment
  const handleCreateAppointment = async () => {
    if (!selectedCar || !selectedShop || !selectedTimeSlot) {
      alert("Te rugăm să selectezi toate datele necesare");
      return;
    }

    setLoading(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      
      // Format the appointment time with Bucharest timezone offset
      // Romania uses UTC+2 (winter) or UTC+3 (summer - EEST)
      const appointmentDate = new Date(selectedTimeSlot.slotStart);
      
      // Check if DST is active (roughly April-October)
      const month = appointmentDate.getMonth();
      const isDST = month >= 3 && month <= 9; // April (3) to October (9)
      const offsetString = isDST ? "+03:00" : "+02:00";
      
      // Format the datetime with timezone offset
      // If slotStart already has timezone info, replace it; otherwise append it
      let appointmentStartFormatted = selectedTimeSlot.slotStart;
      
      // Remove any existing timezone info (Z, +00:00, etc.)
      appointmentStartFormatted = appointmentStartFormatted.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
      
      // Append the Bucharest timezone offset
      appointmentStartFormatted = `${appointmentStartFormatted}${offsetString}`;
      
      const payload = {
        shopId: selectedShop.shopId,
        registrationNumber: selectedCar.plate,
        appointmentStart: appointmentStartFormatted,
        specialization: selectedService,
      };

      console.log('Creating appointment with payload:', payload);

      const response = await axios.post(
        "https://api.servicegest.ro/driver/car/appointment",
        payload,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.id) {
        setAppointmentId(response.data.id);
        setCurrentStep(5); // Move to verification step
      }
    } catch (error) {
      console.error("Error creating appointment:", error);
      alert(
        error.response?.data?.message ||
          "A apărut o eroare la crearea programării. Te rugăm să încerci din nou."
      );
    } finally {
      setLoading(false);
    }
  };

  // Verify appointment
  const handleVerifyAppointment = async (code) => {
    if (!appointmentId || !code) {
      alert("Te rugăm să introduci codul de verificare");
      return;
    }

    setLoading(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      
      const payload = {
        id: appointmentId,
        code: code,
      };

      await axios.post(
        "https://api.servicegest.ro/driver/appointment/confirm",
        payload,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      alert("Programarea ta a fost confirmată cu succes!");
      // Reset and go back to start
      handleChangeFilters();
    } catch (error) {
      console.error("Error verifying appointment:", error);
      alert(
        error.response?.data?.message ||
          "Cod de verificare invalid. Te rugăm să încerci din nou."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchShops = async () => {
    if (!selectedService || !selectedCity || !selectedDate) {
      return;
    }

    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        specialization: selectedService,
        date: selectedDate.format("YYYY-MM-DD"),
        zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        city: selectedCity,
      });

      const response = await fetch(
        `https://api.servicegest.ro/online/slots?${queryParams}`
      );

      if (response.ok) {
        const data = await response.json();
        setShops(data);
        if (isDesktopView && currentStep < 3) {
          setCurrentStep(3);
        }
      }
    } catch (error) {
      console.error("Error fetching shops:", error);
    } finally {
      setLoading(false);
    }
  };

  // Renders the summary bar showing current selections
  const renderSelectionSummary = () => {
    if (!selectedService && !selectedCity && !selectedDate) return null;

    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 mb-6">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Criteriile selectate
            </h3>
            <button
              onClick={handleChangeFilters}
              className="flex items-center gap-1 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 "
            >
              Modifică
            </button>
          </div>

          <div className="flex flex-wrap gap-3">
            {selectedService && (
              <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-3 py-2 rounded-xl border border-purple-200 dark:border-purple-800">
                <WrenchScrewdriverIcon className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {selectedServiceName}
                </span>
              </div>
            )}

            {selectedCity && (
              <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-3 py-2 rounded-xl border border-purple-200 dark:border-purple-800">
                <MapPinIcon className="h-4 w-4" />
                <span className="text-sm font-medium">{selectedCity}</span>
              </div>
            )}

            {selectedDate && (
              <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-3 py-2 rounded-xl border border-purple-200 dark:border-purple-800">
                <CalendarIcon className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {selectedDate.format("DD MMMM YYYY")}
                </span>
              </div>
            )}

            {selectedShop && (
              <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-3 py-2 rounded-xl border border-purple-200 dark:border-purple-800">
                <BuildingOfficeIcon className="h-4 w-4" />
                <span className="text-sm font-medium">{selectedShop.brandName}</span>
              </div>
            )}

            {selectedTimeSlot && (
              <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-3 py-2 rounded-xl border border-purple-200 dark:border-purple-800">
                <ClockIcon className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {new Date(selectedTimeSlot.slotStart).toLocaleTimeString('ro-RO', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Desktop combined view of all steps
  const renderDesktopSelectionPanel = () => {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-visible">
        {/* Header */}
        <div className="px-2 py-2 border-b rounded-t-xl border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-t-xl">
              <MagnifyingGlassIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Găsește un service auto
                </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Completează criteriile pentru a găsi service-uri disponibile
              </p>
            </div>
          </div>
        </div>

        {/* Selection Form */}
        <div className="p-6 overflow-visible">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-visible">
            <div className="space-y-2 relative overflow-visible">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                1. Alege serviciul
              </label>
              <ServiceTypeStep
                serviceTypes={serviceTypes}
                selectedService={selectedService}
                onSelect={(service) => {
                  setSelectedService(service);
                  const serviceName = serviceTypes.find(
                    (s) => s.id === service
                  )?.name;
                  setSelectedServiceName(serviceName || "");
                }}
                isDesktop={true}
              />
            </div>

            <div className="space-y-2 relative overflow-visible">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                2. Alege orașul
              </label>
              <CityStep
                cities={cities}
                selectedCity={selectedCity}
                onSelect={setSelectedCity}
                isDesktop={true}
              />
            </div>

            <div className="space-y-2 relative overflow-visible">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                3. Alege data
              </label>
              <DateStep
                selectedDate={selectedDate}
                onSelect={setSelectedDate}
                isDesktop={true}
              />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={fetchShops}
              disabled={!selectedService || !selectedCity || !selectedDate}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-xl   flex items-center justify-center gap-2"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
              Caută service-uri disponibile
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStep = () => {
    if (isDesktopView) {
      return (
        <div className="space-y-8">
          {/* Selection Panel */}
          {currentStep < 3 && (
            <div className="w-full">{renderDesktopSelectionPanel()}</div>
          )}

          {/* Results - Map and Shop List */}
          {(shops.length > 0 || loading) && currentStep === 3 && (
            <>
              {renderSelectionSummary()}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                    <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                          <MapPinIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                            Hartă Service-uri
                          </h2>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Vizualizează service-urile disponibile pe hartă
                          </p>
                        </div>
                      </div>
                    </div>
                    <MapView
                      shops={shops}
                      loading={loading}
                      selectedCity={selectedCity}
                      selectedDate={selectedDate}
                      onChangeFilters={handleChangeFilters}
                      isDesktopView={isDesktopView}
                    />
                  </div>
                </div>
                <div>
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                    <div className="px-2 py-3 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                          <BuildingStorefrontIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                            Listă Service-uri
                          </h2>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Vezi detaliile service-urilor disponibile
                          </p>
                        </div>
                      </div>
                    </div>
                    <ShopsList
                      shops={shops}
                      loading={loading}
                      selectedService={selectedService}
                      selectedDate={selectedDate}
                      onSelectTimeSlot={handleSelectShopAndTime}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Car Selection Step */}
          {currentStep === 4 && (
            <>
              {renderSelectionSummary()}
              <div className="mx-auto">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                  <div className="px-2 py-4 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <TruckIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                          Alege vehiculul
                        </h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          Selectează vehiculul pentru care dorești programarea
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <CarSelectionStep
                      cars={cars}
                      selectedCar={selectedCar}
                      onSelect={setSelectedCar}
                    />
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setCurrentStep(3)}
                        className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-700 "
                      >
                        Înapoi
                      </button>
                      <button
                        onClick={handleCreateAppointment}
                        disabled={!selectedCar || loading}
                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg "
                      >
                        {loading ? "Se creează..." : "Creează programarea"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Verification Step */}
          {currentStep === 5 && (
            <>
              {renderSelectionSummary()}
              <div className="mx-auto">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                  <div className="px-2 py-4 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <CheckCircleIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                          Confirmă programarea
                        </h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          Introdu codul de verificare primit pe email
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <VerificationStep
                      onVerify={handleVerifyAppointment}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      );
    }

    // Mobile step-by-step flow with improved UI
    switch (currentStep) {
      case 0:
        return (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <WrenchScrewdriverIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Alege serviciul
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Selectează tipul de service necesar
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 overflow-visible">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                1. Alege serviciul
              </label>
              <ServiceTypeStep
                serviceTypes={serviceTypes}
                selectedService={selectedService}
                onSelect={(service) => {
                  setSelectedService(service);
                  const serviceName = serviceTypes.find(
                    (s) => s.id === service
                  )?.name;
                  setSelectedServiceName(serviceName || "");
                }}
              />
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setCurrentStep(1)}
                  disabled={!selectedService}
                  className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg  font-medium"
                >
                  Continuă
                </button>
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="px-2 py-3 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <MapPinIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Alege orașul
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Selectează orașul în care cauți service
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 overflow-visible">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                2. Alege orașul
              </label>
              <CityStep
                cities={cities}
                selectedCity={selectedCity}
                onSelect={setSelectedCity}
              />
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!selectedCity}
                  className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg  font-medium"
                >
                  Continuă
                </button>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="px-2 py-3 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <CalendarIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Alege data
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Selectează data dorită pentru programare
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 overflow-visible">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                3. Alege data
              </label>
              <DateStep
                selectedDate={selectedDate}
                onSelect={setSelectedDate}
              />
              <div className="mt-4 flex justify-end">
                <button
                  onClick={async () => {
                    setCurrentStep(3);
                    await fetchShops();
                  }}
                  disabled={!selectedDate}
                  className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg  font-medium"
                >
                  Caută service-uri
                </button>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <>
            {renderSelectionSummary()}
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="px-2 py-3 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                      <MapPinIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                        Hartă Service-uri
                      </h2>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Vizualizează service-urile disponibile pe hartă
                      </p>
                    </div>
                  </div>
                </div>
                <MapView
                  shops={shops}
                  loading={loading}
                  selectedCity={selectedCity}
                  selectedDate={selectedDate}
                  onChangeFilters={handleChangeFilters}
                />
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="px-2 py-3 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                      <BuildingStorefrontIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                        Listă Service-uri
                      </h2>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Vezi detaliile service-urilor disponibile
                      </p>
                    </div>
                  </div>
                </div>
                <ShopsList
                  shops={shops}
                  loading={loading}
                  selectedService={selectedService}
                  selectedDate={selectedDate}
                  onSelectTimeSlot={handleSelectShopAndTime}
                />
              </div>
            </div>
          </>
        );
      case 4:
        return (
          <>
            {renderSelectionSummary()}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
              <div className="px-2 py-3 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                    <TruckIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                      Alege vehiculul
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Selectează vehiculul pentru care dorești programarea
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <CarSelectionStep
                  cars={cars}
                  selectedCar={selectedCar}
                  onSelect={setSelectedCar}
                />
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-700 "
                  >
                    Înapoi
                  </button>
                  <button
                    onClick={handleCreateAppointment}
                    disabled={!selectedCar || loading}
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg "
                  >
                    {loading ? "Se creează..." : "Creează programarea"}
                  </button>
                </div>
              </div>
            </div>
          </>
        );
      case 5:
        return (
          <>
            {renderSelectionSummary()}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
              <div className="px-2 py-3 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                    <CheckCircleIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                      Confirmă programarea
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Introdu codul de verificare primit pe email
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <VerificationStep
                  onVerify={handleVerifyAppointment}
                  loading={loading}
                />
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col mx-auto">
      {/* Progress Steps - Mobile only, show only for first 3 steps */}

      {/* Step Content */}
      <div className="flex-1 md:px-0">{renderStep()}</div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-2xl flex items-center max-w-sm mx-4">
            <ArrowPathIcon className="h-8 w-8 text-purple-600 animate-spin mr-4" />
            <div>
              <div className="text-slate-800 dark:text-slate-200 font-semibold">
                Se încarcă...
              </div>
              <div className="text-slate-600 dark:text-slate-400 text-sm">
                Căutăm service-urile disponibile
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsMap;
